import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { PaymentSuccess } from "../../lib/gboModels";
import { getDefaultPaymentAmountKind } from "./amountSelection";
import type { PaymentAmountKind, PaymentAmountSelection } from "./amountSelection";
import type { PaymentStep, PendingPayment } from "./checkoutTypes";

type CheckoutState = {
  contextKey: string | null;
  selectedAmount: PaymentAmountKind;
  customAmount: string;
  customAmountTouched: boolean;
  amountSubmitAttempted: boolean;
  step: PaymentStep;
};

type CheckoutContextPayload = {
  contextKey: string;
  invoiceBalance?: number;
  accountBalance: number;
};

const initialState: CheckoutState = {
  contextKey: null,
  selectedAmount: "custom",
  customAmount: "",
  customAmountTouched: false,
  amountSubmitAttempted: false,
  step: { type: "amount" },
};

function resetForContext(state: CheckoutState, payload: CheckoutContextPayload) {
  state.contextKey = payload.contextKey;
  state.selectedAmount = getDefaultPaymentAmountKind(
    payload.invoiceBalance,
    payload.accountBalance,
  );
  state.customAmount = "";
  state.customAmountTouched = false;
  state.amountSubmitAttempted = false;
  state.step = { type: "amount" };
}

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    checkoutCleared: () => initialState,
    checkoutContextEntered(state, action: PayloadAction<CheckoutContextPayload>) {
      if (state.contextKey !== action.payload.contextKey) resetForContext(state, action.payload);
    },
    amountSelectionReset(state, action: PayloadAction<CheckoutContextPayload>) {
      resetForContext(state, action.payload);
    },
    amountSelected(state, action: PayloadAction<PaymentAmountKind>) {
      state.selectedAmount = action.payload;
    },
    customAmountChanged(state, action: PayloadAction<string>) {
      state.customAmount = action.payload;
      state.customAmountTouched = false;
      state.amountSubmitAttempted = false;
    },
    customAmountTouched(state) {
      state.customAmountTouched = true;
    },
    amountSubmitted(state) {
      state.amountSubmitAttempted = true;
    },
    cardStepStarted(state, action: PayloadAction<PaymentAmountSelection>) {
      state.step = { type: "card", paymentAmount: action.payload };
    },
    amountStepReturned(state) {
      state.step = { type: "amount" };
    },
    paymentReady(
      state,
      action: PayloadAction<{
        paymentAmount: PaymentAmountSelection;
        pendingPayment: PendingPayment;
      }>,
    ) {
      state.step = {
        type: "confirm",
        paymentAmount: action.payload.paymentAmount,
        pendingPayment: action.payload.pendingPayment,
      };
    },
    cardStepReturned(state) {
      if (state.step.type === "confirm")
        state.step = { type: "card", paymentAmount: state.step.paymentAmount };
    },
    paymentSucceeded(state, action: PayloadAction<PaymentSuccess>) {
      state.step = { type: "success", success: action.payload };
    },
  },
});

export const {
  checkoutCleared,
  amountSelected,
  amountSelectionReset,
  amountStepReturned,
  amountSubmitted,
  cardStepReturned,
  cardStepStarted,
  checkoutContextEntered,
  customAmountChanged,
  customAmountTouched,
  paymentReady,
  paymentSucceeded,
} = checkoutSlice.actions;

export const checkoutReducer = checkoutSlice.reducer;
